// Package Imports
import { dereferenceSync } from "dereference-json-schema";
import { JSONSchema6 } from "json-schema";

/**
 * Get a value from an object using a path like ['properties', 'bid']
 */
const getByPath = (obj: any, path: string[]): any => {
  let current = obj;
  for (const key of path) {
    if (current == null || typeof current !== "object") return undefined;
    current = current[key];
  }
  return current;
};

/**
 * Recursively collect paths to all schemas with $ref and their sibling properties
 */
const collectRefSiblings = (
  schema: any,
  path: string[] = [],
  preservation: Map<string, Record<string, any>>
): void => {
  if (!schema || typeof schema !== "object") return;

  // If this schema has $ref with sibling properties, preserve them by path
  if (schema.$ref) {
    const siblings: Record<string, any> = {};
    for (const key in schema) {
      if (key !== "$ref") {
        siblings[key] = schema[key];
      }
    }
    if (Object.keys(siblings).length > 0) {
      preservation.set(path.join("."), siblings);
    }
  }

  // Recurse into nested schemas
  if (schema.$defs) {
    Object.keys(schema.$defs).forEach((key) => {
      collectRefSiblings(schema.$defs[key], [...path, "$defs", key], preservation);
    });
  }
  if (schema.properties) {
    Object.keys(schema.properties).forEach((key) => {
      collectRefSiblings(schema.properties[key], [...path, "properties", key], preservation);
    });
  }
  if (schema.items) {
    collectRefSiblings(schema.items, [...path, "items"], preservation);
  }
  if (schema.additionalProperties && typeof schema.additionalProperties === "object") {
    collectRefSiblings(schema.additionalProperties, [...path, "additionalProperties"], preservation);
  }
  if (Array.isArray(schema.oneOf)) {
    schema.oneOf.forEach((s: any, idx: number) => {
      collectRefSiblings(s, [...path, "oneOf", String(idx)], preservation);
    });
  }
  if (Array.isArray(schema.anyOf)) {
    schema.anyOf.forEach((s: any, idx: number) => {
      collectRefSiblings(s, [...path, "anyOf", String(idx)], preservation);
    });
  }
  if (Array.isArray(schema.allOf)) {
    schema.allOf.forEach((s: any, idx: number) => {
      collectRefSiblings(s, [...path, "allOf", String(idx)], preservation);
    });
  }
};

/**
 * Apply preserved properties back to the dereferenced schema
 * Note: After dereferencing, multiple properties might point to the same object.
 * We need to clone before assigning to avoid overwriting previous restorations.
 */
const applyRefSiblings = (
  schema: any,
  preservation: Map<string, Record<string, any>>
): void => {
  preservation.forEach((siblings, pathStr) => {
    const path = pathStr.split(".").filter(Boolean);
    if (path.length === 0) return;

    const parentPath = path.slice(0, -1);
    const propertyKey = path[path.length - 1];
    const parent = getByPath(schema, parentPath);

    if (parent && typeof parent === "object" && parent[propertyKey]) {
      const target = parent[propertyKey];
      if (typeof target === "object") {
        const merged: any = { ...target, ...siblings };
        // Deep-merge properties and required so $ref fields aren't overwritten by
        // discriminator siblings (e.g. oneOf branches that add event_type alongside $ref).
        if (target.properties && siblings.properties) {
          merged.properties = { ...target.properties, ...siblings.properties };
        }
        if (Array.isArray(target.required) || Array.isArray(siblings.required)) {
          merged.required = [
            ...new Set([
              ...(Array.isArray(target.required) ? target.required : []),
              ...(Array.isArray(siblings.required) ? siblings.required : []),
            ]),
          ];
        }
        parent[propertyKey] = merged;
      }
    }
  });
};

export const dereferenceJSON: any = async (schema: JSONSchema6) => {
  try {
    // Deep clone to avoid mutating original
    const cloned = JSON.parse(JSON.stringify(schema));

    // Collect all $ref locations and their sibling properties
    const preservation = new Map<string, Record<string, any>>();
    collectRefSiblings(cloned, [], preservation);

    // Dereference
    const dereferenced = dereferenceSync(cloned);

    // Restore preserved properties (e.g., title, description alongside $ref)
    applyRefSiblings(dereferenced, preservation);

    return {
      data: dereferenced,
      message: null,
      error: null,
    };
  } catch (e) {
    const err = e instanceof Error ? e : new Error(String(e));
    return {
      data: null,
      message: err.message || 'Error dereferencing JSON Schema.',
      error: err,
    };
  }
};
